import { Injectable } from '@angular/core';
import { MapPoint } from './models/map-model';
import { Floor } from '../../models/project.model';
import { REPORT_TYPE } from '../project/const';
import { RefactoredReport } from '../../models/reports-refactored.model';
import { ViewerModeService } from './viewer-mode.service';

@Injectable({
  providedIn: 'root',
})
export class ViewerBimServiceService {
  private _bimViewer: any;
  public currentFloor: Floor;
  public currentReport: RefactoredReport | any;
  private target: THREE.Vector3;
  constructor(private viewerMode: ViewerModeService) {}

  set bimViewer(viewer: Autodesk.Viewing.Viewer3D) {
    this._bimViewer = viewer;
  }
  get bimViewer() {
    return this._bimViewer;
  }

  // public syncBimCameraWith360Viewer(target: { x: number; y: number; z: number }): any {
  //   const camera = this._bimViewer?.navigation?.getCamera();
  //   if (camera !== undefined) {
  //     this._bimViewer?.navigation?.setTarget(new THREE.Vector3(target.z, target.x, target.y));
  //     this._bimViewer?.navigation?.setCameraUpVector(new THREE.Vector3(0, 0, 1));
  //   }
  // }

  //updating BIM camera corresponding to Krpano viewer syncing
  public syncBimCameraWith360Viewer(
    target: {
      lat: number;
      lon: number;
      phi: number;
      theta: number;
    },
    bimOffset?: number,
  ): any {
    if (!this.viewerMode.isBimLocked) return;
    const camera = this._bimViewer?.navigation?.getCamera();
    if (camera !== undefined) {
      const radius = 500;

      // For syncing, mirror the 360 viewer's orientation
      // Images: apply bim_offset to match updateBimCamWithTarget
      // Videos: apply Math.PI baseAlignment for coordinate system match
      const isVideo = this.currentReport?.report_type === REPORT_TYPE.VIDEOS;
      const offset = bimOffset || 0;
      const bimOffsetRad = offset * (Math.PI / 180);
      
      // Apply floor rotation to match updateBimCamWithTarget
      const floorRotation = this.currentFloor?.rotation_angle_degrees 
        ? parseFloat(this.currentFloor.rotation_angle_degrees) * (Math.PI / 180)
        : 0;
      
      const baseAlignment = isVideo ? Math.PI : 0;
      const adjustedTheta = target.theta + floorRotation + baseAlignment + (isVideo ? 0 : bimOffsetRad);

      const threeTarget = new THREE.Vector3(
        radius * Math.sin(target.phi) * Math.cos(adjustedTheta),
        radius * Math.cos(target.phi),
        radius * Math.sin(target.phi) * Math.sin(adjustedTheta),
      );

      // Convert Three.js (Y-up) to BIM viewer coordinates (assumed Z-up)
      // Swap Y and Z: Three.js (x, y, z) becomes BIM viewer (x, z, y)
      const bimTarget = {
        x: -threeTarget.x,
        y: threeTarget.z,
        z: threeTarget.y,
      };
      this._bimViewer?.navigation?.setTarget(bimTarget);

      // this._bimViewer?.navigation?.setView(newTarget, this.target);

      this._bimViewer?.navigation?.setCameraUpVector(new THREE.Vector3(0, 0, 1));
    }
  }
  async captureForgeScreenshot(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this._bimViewer?.getScreenShot(1920, 1080, async (result) => {
        this.blobUrlToBase64(result)
          .then((base64) => {
            resolve(base64);
          })
          .catch((error) => {});
      });
    });
  }
  async blobUrlToBase64(blobUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Fetch the blob content
      fetch(blobUrl)
        .then((response) => response.blob()) // Convert response to a Blob
        .then((blob) => {
          // Use FileReader to convert Blob to Base64
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string); // Convert result to Base64
          reader.onerror = (error) => reject('Error reading blob: ' + error);
          reader.readAsDataURL(blob); // Read the Blob as a Data URL
        })
        .catch((error) => reject('Error fetching blob: ' + error));
    });
  }
  // this is used to change camera position
  updateBimCamWithTarget(mapPoint: MapPoint, currentViewerTheta?: number): any {
    if (this._bimViewer === undefined) return;

    const x = parseFloat(mapPoint.bim_point_x);
    const y = parseFloat(mapPoint.bim_point_y);
    const z = parseFloat(mapPoint.bim_point_z);
    let rotationAngle = this.currentFloor.rotation_angle_degrees
      ? parseFloat(this.currentFloor.rotation_angle_degrees)
      : 0;

    const headingAngle = mapPoint.heading_angle;
    // Convert angles to radians
    const hlookatRad = rotationAngle * (Math.PI / 180);
    const vlookatRad = 0;
    const isVideo = this.currentReport.report_type === REPORT_TYPE.VIDEOS;
    const baseAlignment = isVideo ? Math.PI : 0; // apply 180° for video reports only
    
    // Use current viewer orientation if provided (for synced mode) to match syncBimCameraWith360Viewer
    let headingRad: number;
    if (currentViewerTheta !== undefined) {
      // Use live orientation from 360 viewer (matches sync logic)
      const offset = (mapPoint.bim_offset || 0) * (Math.PI / 180);
      headingRad = currentViewerTheta + baseAlignment + (isVideo ? 0 : offset);
    } else {
      // Use stored heading (for non-synced scenarios)
      const headingDeg = isVideo ? headingAngle : (mapPoint.bim_offset || 0);
      headingRad = headingDeg * (Math.PI / 180) + baseAlignment;
    }

    // Calculate the direction vector using trigonometry
    const dx = Math.cos(vlookatRad) * Math.cos(hlookatRad + headingRad);
    const dy = Math.cos(vlookatRad) * Math.sin(hlookatRad + headingRad);
    const dz = Math.sin(vlookatRad);

    // Calculate the target point by adding the deltas to the position
    this.target = new THREE.Vector3(x + dx, y + dy, z + dz);

    this._bimViewer?.navigation?.setView(new THREE.Vector3(x, y, z), this.target);
    this._bimViewer?.navigation?.setCameraUpVector(new THREE.Vector3(0, 0, 1));

    this._bimViewer?.navigation?.setFocalLength(7.2);
  }
  normalizeAngle(angle) {
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
  }
}
